export async function getJsonApi<T1, T2 = any>(path: string, data?: T2): Promise<T1> {
    const response: Response = await fetch(
        `${viteConfiguration.API_URL}${path}${data ? `?${new URLSearchParams(data).toString()}` : ""}`,
        {
            method: "GET",
            headers: {
                "X-PlayerSessionId": await getPlayerSessionId(),
            },
        },
    );

    if (parseInt(response.headers.get("content-length")!) > 0) {
        return response.json() as T1;
    }

    //NOTE: Trick TypeScrupt into returning a void
    return Promise.resolve(undefined as T1);
}

export async function postJsonApi<T1, T2>(path: string, data?: T2): Promise<T1> {
    return methodJsonApi("POST", path, data);
}

export async function methodJsonApi<T1, T2>(method: string, path: string, data?: T2): Promise<T1> {
    const response: Response = await fetch(`${viteConfiguration.API_URL}${path}${data ? `?${new URLSearchParams(data).toString()}` : ""}`, {
        method: method,
        headers: {
            "Content-Type": "application/json",
            "X-PlayerSessionId": await getPlayerSessionId(),
        },
        body: data ? JSON.stringify(data) : undefined,
    });

    if (parseInt(response.headers.get("content-length")!) > 0) {
        return response.json() as T1;
    }

    //NOTE: Trick TypeScript into returning a void
    return Promise.resolve(undefined as T1);
}

async function getPlayerSessionId(): Promise<string> {
    let playerSessionId: string | null = localStorage.getItem("playerSessionId");

    if (!playerSessionId) {
        playerSessionId = await generateHWID();

        localStorage.setItem("playerSessionId", playerSessionId);
    }

    return playerSessionId;
}

/**
 * The function generates a unique hardware identifier (HWID) based on browser and display properties.
 * The HWID is an SHA-256 hash of a collection of properties pulled from the browser's navigator and screen objects,
 * and the WebGL vendor and renderer.
 * @returns {Promise<string>} A promise that resolves to an SHA-256 hash string.
 */
async function generateHWID(): Promise<string> {
    const { userAgent, language, hardwareConcurrency } = window.navigator;
    const { height, width, colorDepth } = window.screen;

    // Get the timezone offset
    const timezoneOffset: number = new Date().getTimezoneOffset();
    // Get the WebGL vendor and renderer
    const webglVendorAndRenderer: string = getWebglVendorAndRenderer();

    // Gather all parts into an array
    const uidParts: (string | number)[] = [
        userAgent,
        language,
        hardwareConcurrency,
        width,
        height,
        colorDepth,
        webglVendorAndRenderer,
        timezoneOffset,
    ];

    // Join all parts with a ':' character
    const hardwareIdentifier: string = uidParts.join(":");

    // Use TextEncoder to encode the string into bytes
    const encoder: TextEncoder = new TextEncoder();
    // Perform SHA-256 hash
    const digest: ArrayBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(hardwareIdentifier));
    // Convert ArrayBuffer to hexadecimal string
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

/**
 * This function creates a WebGL rendering context and returns
 * the WebGL vendor and renderer as a string separated by a '|'.
 * @returns {string} WebGL vendor and renderer or 'null' if WebGL context could not be established.
 */
function getWebglVendorAndRenderer(): string {
    const canvas: HTMLCanvasElement = document.createElement("canvas");
    const gl: WebGLRenderingContext =
        (canvas.getContext("webgl") as WebGLRenderingContext) ||
        (canvas.getContext("experimental-webgl") as WebGLRenderingContext);

    // Return 'null' if WebGL context couldn't be established
    if (!gl) {
        return "null";
    }

    // Retrieve and return the WebGL vendor and renderer details
    return `${gl.getParameter(gl.VENDOR)}|${gl.getParameter(gl.RENDERER)}`;
}
